import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { showCurrency } from '../../utils';
import { FaFileExcel } from 'react-icons/fa';
import { useLabels } from '../../hooks/useLabels';
import { useTrailNavigate } from '../../context/EntityTrailContext';
import DebtDeletionModal from '../../components/DebtDeletionModal/DebtDeletionModal';
import { Section, StatStrip, DataTable, IdChip, Pill, RowActionsMenu, Button, ExportModal, buildXlsxExport, exportAmount, TrailLink } from '../../components/EntityList';
import { useClientSort } from '../../hooks/useClientSort';

function formatDate(date) {
	return date ? new Date(date).toLocaleDateString('es-UY') : '';
}

function formatMoney(currency, amount) {
	return `${showCurrency(currency) || ''} ${Number(amount || 0).toLocaleString('es-UY')}`.trim();
}

function AssetSalesDebts({ rows, loading, error, onRefetch }) {
	const { t } = useLabels();
	const { id } = useParams();
	const navigate = useNavigate();
	const navigateWithTrail = useTrailNavigate();
	const [deletingDebt, setDeletingDebt] = useState(null);
	const [exportOpen, setExportOpen] = useState(false);

	const { sortedRows, sortColumn, direction, onSort } = useClientSort(rows, {
		defaultColumn: 'customId',
		accessors: { customId: (debt) => debt.customId },
	});

	const stats = useMemo(() => {
		const totalByCurrency = new Map();
		rows.forEach((debt) => {
			const key = debt.currency || '—';
			totalByCurrency.set(key, (totalByCurrency.get(key) || 0) + (debt.capital || 0));
		});
		return [
			{ label: t('entity.debt.plural'), value: rows.length },
			{
				label: t('field.capital'),
				value: [...totalByCurrency.entries()].map(([currency, total]) => formatMoney(currency, total)).join(' · ') || '—',
				mono: true,
			},
		];
	}, [rows, t]);

	function newDebtHandler() {
		navigate(`/AdminScreen/assetSales/assetSale/${id}/newDebt`);
	}

	function editDebtHandler(debt) {
		navigate(`/AdminScreen/assetSales/assetSale/${id}/debts/${debt._id}/edit`);
	}

	const exportFields = [
		{ key: 'customId', label: t('field.accountConcept'), locked: true },
		{ key: 'owner', label: t('entity.account.debtor') },
		{ key: 'currency', label: t('field.currency') },
		{ key: 'capital', label: t('field.capital') },
		{ key: 'concept', label: t('entity.concept') },
		{ key: 'dues', label: t('entity.due.plural') },
		{ key: 'amount', label: t('field.dueAmount') },
		{ key: 'date', label: t('field.date') },
		{ key: 'endDate', label: t('field.endDate') },
		{ key: 'periodicity', label: t('field.periodicity') },
	];

	const exportGetters = {
		customId: (debt) => debt.customId || '',
		owner: (debt) => debt.owner?.name || '',
		currency: (debt) => debt.currency || '',
		capital: (debt) => exportAmount(debt.capital),
		concept: (debt) => debt.concept || '',
		dues: (debt) => debt.dues ?? '',
		amount: (debt) => exportAmount(debt.amount),
		date: (debt) => formatDate(debt.date),
		endDate: (debt) => (debt.endDate ? formatDate(debt.endDate) : ''),
		periodicity: (debt) => debt.periodicity || '',
	};

	const handleExport = (selectedKeys) =>
		buildXlsxExport({
			rows: sortedRows,
			fields: exportFields,
			selectedKeys,
			getters: exportGetters,
			sheetName: 'Deudas',
			filePrefix: 'deudas',
		});

	const columns = [
		{
			key: 'customId',
			header: t('field.accountConcept'),
			sortable: true,
			render: (debt) => (
				<TrailLink to={`/AdminScreen/debts/debt/${debt._id}`} onClick={(e) => e.stopPropagation()}>
					<IdChip>{debt.customId}</IdChip>
				</TrailLink>
			),
		},
		{ key: 'owner', header: t('entity.account.debtor'), render: (debt) => debt.owner?.name },
		{
			key: 'capital',
			header: t('field.capital'),
			className: 'tw:text-right',
			render: (debt) => <span className='tw:font-mono tw:tabular-nums'>{formatMoney(debt.currency, debt.capital)}</span>,
		},
		{ key: 'concept', header: t('entity.concept'), render: (debt) => <Pill tone='neutral'>{debt.concept}</Pill> },
		{ key: 'dues', header: t('entity.due.plural'), className: 'tw:font-mono tw:text-right', render: (debt) => debt.dues },
		{
			key: 'amount',
			header: t('field.dueAmount'),
			className: 'tw:text-right',
			render: (debt) => <span className='tw:font-mono tw:tabular-nums'>{formatMoney(debt.currency, debt.amount)}</span>,
		},
		{ key: 'date', header: t('field.date'), className: 'tw:font-mono', render: (debt) => formatDate(debt.date) },
		{
			key: 'endDate',
			header: t('field.endDate'),
			className: 'tw:font-mono',
			render: (debt) => (debt.endDate ? formatDate(debt.endDate) : <span className='tw:text-ink-4'>—</span>),
		},
		{ key: 'periodicity', header: t('field.periodicity'), render: (debt) => debt.periodicity },
	];

	const renderRowActions = (debt) => (
		<RowActionsMenu
			items={[
				{ label: t('action.edit'), onClick: () => editDebtHandler(debt) },
				{ label: t('action.delete'), onClick: () => setDeletingDebt(debt), danger: true },
			]}
		/>
	);

	return (
		<div className='tw:flex tw:flex-col tw:gap-4'>
			<StatStrip stats={stats} />
			<div className='tw:flex tw:justify-end tw:gap-2'>
				<Button size='sm' icon={<FaFileExcel />} onClick={() => setExportOpen(true)} disabled={rows.length === 0}>
					{t('action.export')}
				</Button>
				<Button size='sm' variant='primary' onClick={newDebtHandler}>
					{t('action.add')} {t('entity.debt')}
				</Button>
			</div>
			<Section title={t('entity.debt.plural')}>
				<DataTable
					columns={columns}
					rows={sortedRows}
					loading={loading}
					error={error}
					sortColumn={sortColumn}
					direction={direction}
					onSort={onSort}
					onRowClick={(debt) => navigateWithTrail(`/AdminScreen/debts/debt/${debt._id}`)}
					rowActions={renderRowActions}
					emptyMessage={t('msg.noData')}
				/>
			</Section>

			<ExportModal
				show={exportOpen}
				onClose={() => setExportOpen(false)}
				count={rows.length}
				fields={exportFields}
				subtitle={t('entity.debt.plural')}
				onExport={handleExport}
			/>

			<DebtDeletionModal
				show={Boolean(deletingDebt)}
				debt={deletingDebt}
				onClose={() => setDeletingDebt(null)}
				onDeleted={() => {
					setDeletingDebt(null);
					onRefetch();
				}}
			/>
		</div>
	);
}

export default AssetSalesDebts;
